import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  signal,
  viewChild,
  ElementRef,
} from '@angular/core';

export type UiMediaUploadKind = 'image' | 'video';
export type UiMediaUploadAccept = 'media' | 'image';

export interface UiMediaUploadItem {
  readonly id: string;
  readonly file: File;
  readonly kind: UiMediaUploadKind;
  readonly url: string;
}

let mediaUploadId = 0;

/**
 * Molécula para seleccionar y previsualizar imágenes y videos.
 * Es presentacional: conserva los File en memoria y no realiza peticiones HTTP.
 */
@Component({
  selector: 'ui-media-upload',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './media-upload.component.html',
  styleUrl: './media-upload.component.scss',
  host: { class: 'ui-media-upload-host' },
})
export class UiMediaUploadComponent {
  readonly value = model<readonly UiMediaUploadItem[]>([]);
  readonly label = input(
    'Para una mejor atención apóyanos con fotos o videos acerca de tu solicitud de servicio',
  );
  readonly selectLabel = input('Seleccionar');
  readonly required = input(false);
  readonly showOptionalHint = input(false);
  readonly requiredHint = input('Obligatorio');
  readonly optionalHint = input('Opcional');
  readonly disabled = input(false);
  /** `null` = sin límite. Con `1`, el selector se oculta al tener archivo. */
  readonly maxFiles = input<number | null>(null);
  /** `media` = imagen y video. `image` = solo imagen. */
  readonly accept = input<UiMediaUploadAccept>('media');

  protected readonly inputId = `ui-media-upload-${++mediaUploadId}`;
  protected readonly dragging = signal(false);
  protected readonly acceptAttr = computed(() =>
    this.accept() === 'image' ? 'image/*' : 'image/*,video/*',
  );
  protected readonly multiple = computed(() => {
    const max = this.maxFiles();
    return max == null || max > 1;
  });
  protected readonly canAdd = computed(() => {
    const max = this.maxFiles();
    return max == null || this.value().length < max;
  });
  protected readonly pickerAriaLabel = computed(() =>
    this.accept() === 'image'
      ? `${this.selectLabel()} foto`
      : `${this.selectLabel()} fotos o videos`,
  );
  protected readonly showRequirement = computed(
    () => this.required() || this.showOptionalHint(),
  );
  protected readonly requirementText = computed(() =>
    this.required() ? this.requiredHint() : this.optionalHint(),
  );
  private readonly fileInput =
    viewChild<ElementRef<HTMLInputElement>>('fileInput');
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    let previousItems: readonly UiMediaUploadItem[] = [];

    effect(() => {
      const currentItems = this.value();
      for (const previous of previousItems) {
        if (!currentItems.some(current => current.id === previous.id)) {
          URL.revokeObjectURL(previous.url);
        }
      }
      previousItems = currentItems;
    });

    this.destroyRef.onDestroy(() => {
      for (const item of this.value()) {
        URL.revokeObjectURL(item.url);
      }
    });
  }

  protected openPicker(): void {
    if (this.disabled() || !this.canAdd()) return;
    this.fileInput()?.nativeElement.click();
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.openPicker();
  }

  protected onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(input.files);
    input.value = '';
  }

  protected onDragOver(event: DragEvent): void {
    if (this.disabled()) return;
    event.preventDefault();
    this.dragging.set(true);
  }

  protected onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(false);
  }

  protected onDrop(event: DragEvent): void {
    if (this.disabled()) return;
    event.preventDefault();
    this.dragging.set(false);
    this.addFiles(event.dataTransfer?.files ?? null);
  }

  protected view(item: UiMediaUploadItem): void {
    window.open(item.url, '_blank', 'noopener,noreferrer');
  }

  protected download(item: UiMediaUploadItem): void {
    const link = document.createElement('a');
    link.href = item.url;
    link.download = item.file.name;
    link.click();
  }

  protected remove(item: UiMediaUploadItem): void {
    if (this.disabled()) return;
    this.value.set(this.value().filter(current => current.id !== item.id));
  }

  protected onVideoMetadata(event: Event): void {
    const video = event.target as HTMLVideoElement;
    if (video.currentTime === 0) {
      video.currentTime = 0.1;
    }
  }

  private addFiles(fileList: FileList | null): void {
    if (!fileList?.length) return;

    const imagesOnly = this.accept() === 'image';
    const next = Array.from(fileList)
      .filter(file =>
        imagesOnly
          ? file.type.startsWith('image/')
          : file.type.startsWith('image/') || file.type.startsWith('video/'),
      )
      .map<UiMediaUploadItem>(file => ({
        id: `${this.inputId}-${crypto.randomUUID()}`,
        file,
        kind: file.type.startsWith('video/') ? 'video' : 'image',
        url: URL.createObjectURL(file),
      }));

    if (!next.length) return;

    const max = this.maxFiles();
    const current = this.value();
    const remaining = max == null ? next.length : Math.max(0, max - current.length);
    const items = next.slice(0, remaining);
    if (items.length) {
      this.value.set([...current, ...items]);
    }
  }
}
